/**
 * Idle Timeout Utility
 *
 * Monitors user activity (mouse, keyboard, scroll, touch, click).
 * If no activity is detected within the configured timeout period,
 * the provided callback is invoked (typically to log the user out).
 */

// Default: 15 minutes in milliseconds
const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000;

// Events that count as "user activity"
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel',
];

let timerId = null;
let onTimeoutCallback = null;
let timeoutMs = DEFAULT_TIMEOUT_MS;
let isRunning = false;

/**
 * Reset the idle timer — called on every user activity event
 */
function resetTimer() {
  if (!isRunning) return;
  if (timerId) {
    clearTimeout(timerId);
  }
  timerId = setTimeout(() => {
    if (onTimeoutCallback) {
      onTimeoutCallback();
    }
  }, timeoutMs);
}

/**
 * Start monitoring user activity.
 * @param {Function} callback - Function to call when idle timeout is reached
 * @param {number} [timeout] - Timeout in milliseconds (default: 15 minutes)
 */
export function startIdleTimer(callback, timeout) {
  if (isRunning) {
    stopIdleTimer();
  }

  onTimeoutCallback = callback;
  timeoutMs = timeout || DEFAULT_TIMEOUT_MS;
  isRunning = true;

  // Attach activity listeners
  ACTIVITY_EVENTS.forEach((event) => {
    document.addEventListener(event, resetTimer, { passive: true });
  });

  // Start the initial timer
  resetTimer();
}

/**
 * Stop monitoring user activity and clean up all listeners/timers.
 */
export function stopIdleTimer() {
  isRunning = false;

  if (timerId) {
    clearTimeout(timerId);
    timerId = null;
  }

  ACTIVITY_EVENTS.forEach((event) => {
    document.removeEventListener(event, resetTimer);
  });

  onTimeoutCallback = null;
}
